import React from 'react';
import { usePersonalFormPresenter } from './PersonalForm.presenter';

import { AlertPopup } from '../../AlertPopup/AlertPopup';

import { InputMask } from 'utils/inputMask';
import { VOID_FUNCTION } from 'constants';
import {
  ALERT_POPUP_TEXT,
  KEY_COMPONENTS,
} from 'components/modules/MainAnketa/MainAnketa.constants';
import {
  NAMES,
  PERSONAL_FORM_LABELS,
  PREVIOUS_MIDDLE_NAME_STRUCTURE,
} from './PersonalForm.constants';

import * as S from './PersonalForm.styles';

export const PersonalForm = ({
  persona,
  validate,
  errors,
  confidenceErrors,
  clearConfidenceErrors,
  isContentHidden = false,
  disabled = false,
  setFocusAt,
  onChange = VOID_FUNCTION,
}) => {
  const presenter = usePersonalFormPresenter({
    persona,
    validate,
    errors,
    confidenceErrors,
    clearConfidenceErrors,
    setFocusAt,
    onChange,
  });

  const { personalFormValues, contactsFormValues, workFormValues } = presenter;

  if (!personalFormValues) {
    return null;
  }

  return (
    <S.FormGridRow>
      <S.FioUser
        id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.PERSON_FULL_NAME}`}
        tabIndex={'-1'}
        ref={presenter.fullNameRef}
        label={PERSONAL_FORM_LABELS[NAMES.PERSON_FULL_NAME]}
        valueGender={personalFormValues[NAMES.PERSON_GENDER]}
        defaultValue={personalFormValues[NAMES.PERSON_FULL_NAME]}
        error={presenter.getError(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_FULL_NAME}`)}
        confidenceError={
          personalFormValues[NAMES.PERSON_FULL_NAME] &&
          presenter.getConfidenceError(
            `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_FULL_NAME}`
          )
        }
        required
        dataTest="fio"
        changeHandler={presenter.changeHandlerFio}
        onBlur={() => {
          presenter.startExtendedValidation(
            `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_FULL_NAME}`,
            NAMES.PERSON_MIDDLE_NAME
          );
        }}
      />

      <S.MobilePhone
        id={`${KEY_COMPONENTS.CONTACTS}_${NAMES.PERSON_MOBILE_PHONE}`}
        tabIndex="1"
        label={PERSONAL_FORM_LABELS[NAMES.PERSON_MOBILE_PHONE]}
        name={NAMES.PERSON_MOBILE_PHONE}
        defaultValue={
          isContentHidden
            ? InputMask.CELLPHONE_PRIVATE_NEW[0].hidden
            : contactsFormValues[NAMES.PERSON_MOBILE_PHONE]
        }
        val={
          isContentHidden
            ? InputMask.CELLPHONE_PRIVATE_NEW[0].hidden
            : contactsFormValues[NAMES.PERSON_MOBILE_PHONE]
        }
        mask={
          isContentHidden
            ? InputMask.CELLPHONE_PRIVATE_NEW[0].hidden
            : InputMask.CELLPHONE_PRIVATE_NEW
        }
        error={presenter.getError(`${KEY_COMPONENTS.CONTACTS}.${NAMES.PERSON_MOBILE_PHONE}`)}
        required
        disabled={disabled || isContentHidden}
        onChange={presenter.changeHandlerPhone}
        onBlur={() =>
          presenter.validation(`${KEY_COMPONENTS.CONTACTS}.${NAMES.PERSON_MOBILE_PHONE}`)
        }
      />

      <S.ChangeFioToogle
        name="isFioChanged"
        value={personalFormValues[NAMES.PERSON_FIO_CHANGED]}
        otherError={presenter.getError(
          `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_FULL_NAME}`
        )}
        disabled={disabled}
        dataTest="isFioChanged"
        onChange={presenter.changeHandlerFioCheckbox}
      >
        {PERSONAL_FORM_LABELS[NAMES.PERSON_FIO_CHANGED]}
      </S.ChangeFioToogle>

      {personalFormValues[NAMES.PERSON_FIO_CHANGED] && (
        <S.PrevFioUser
          id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.PREVIOUS_PERSON_FULL_NAME}`}
          structure={PREVIOUS_MIDDLE_NAME_STRUCTURE}
          tabIndex={'2'}
          label={PERSONAL_FORM_LABELS[NAMES.PERSON_FULL_NAME]}
          valueGender={personalFormValues[NAMES.PERSON_GENDER]}
          defaultValue={personalFormValues[NAMES.PREVIOUS_PERSON_FULL_NAME]}
          error={presenter.getError(
            `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PREVIOUS_PERSON_FULL_NAME}`
          )}
          required
          disabled={disabled}
          changeHandler={presenter.changeHandlerPrevFio}
          onBlur={() => {
            presenter.startExtendedValidation(
              `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PREVIOUS_PERSON_FULL_NAME}`,
              NAMES.PREVIOUS_PERSON_MIDDLE_NAME
            );
          }}
        />
      )}

      <S.MaritalStatus
        id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.PERSON_MARITAL_STATUS}_sysName`}
        tabIndex="5"
        label={PERSONAL_FORM_LABELS[NAMES.PERSON_MARITAL_STATUS]}
        name={NAMES.PERSON_MARITAL_STATUS}
        defaultValue={personalFormValues[NAMES.PERSON_MARITAL_STATUS]}
        error={presenter.getError(
          `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_MARITAL_STATUS}.sysName`
        )}
        disabled={disabled}
        list={
          presenter.martialStatusesList &&
          presenter.martialStatusesList.filter((item) => item.id !== 81)
        }
        onlyid={false}
        required
        onChange={presenter.changeHandler}
        onBlur={() => {
          presenter.validation(
            `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_MARITAL_STATUS}.sysName`
          );
          presenter.validation(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_SPOUSE}`);
          presenter.validation(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_TRUSTEES}`);
        }}
      />

      <S.ChildrenCount
        id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.PERSON_CHILDREN_COUNT}`}
        tabIndex="6"
        label={PERSONAL_FORM_LABELS[NAMES.PERSON_CHILDREN_COUNT]}
        name={NAMES.PERSON_CHILDREN_COUNT}
        val={personalFormValues[NAMES.PERSON_CHILDREN_COUNT]}
        defaultValue={personalFormValues[NAMES.PERSON_CHILDREN_COUNT]}
        mask={InputMask.INTEGER}
        error={presenter.getError(
          `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_CHILDREN_COUNT}`
        )}
        required
        disabled={disabled}
        onChange={presenter.changeHandlerChildrenCount}
        onBlur={() =>
          presenter.validation(
            `${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.PERSON_CHILDREN_COUNT}`
          )
        }
      />

      <S.Education
        id={`${KEY_COMPONENTS.WORK}_${NAMES.PERSON_EDUCATION}_sysName`}
        tabIndex="63"
        label={PERSONAL_FORM_LABELS[NAMES.PERSON_EDUCATION]}
        name={NAMES.PERSON_EDUCATION}
        defaultValue={workFormValues[NAMES.PERSON_EDUCATION]}
        list={presenter.educationList}
        error={presenter.getError(`${KEY_COMPONENTS.WORK}.${NAMES.PERSON_EDUCATION}.sysName`)}
        onlyid={false}
        required
        disabled={disabled}
        onChange={presenter.changeHandlerEducation}
        onBlur={() =>
          presenter.validation(`${KEY_COMPONENTS.WORK}.${NAMES.PERSON_EDUCATION}.sysName`)
        }
      />

      {(presenter.activeAlert === NAMES.PERSON_MIDDLE_NAME ||
        presenter.activeAlert === NAMES.PREVIOUS_PERSON_MIDDLE_NAME) && (
        <AlertPopup
          title={ALERT_POPUP_TEXT.MIDDLE_NAME.title}
          description={ALERT_POPUP_TEXT.MIDDLE_NAME.description}
          onAgree={presenter.handleMiddleNameAgree}
          onCancel={presenter.handleMiddleNameCancel}
          onClose={presenter.handleMiddleNameCancel}
        />
      )}

      {presenter.activeAlert === 'fio' && (
        <AlertPopup
          title={ALERT_POPUP_TEXT.FIO.title}
          description={ALERT_POPUP_TEXT.FIO.description}
          dataTest="recognizeAlert"
          dataTestButton="okButton"
          onAgree={presenter.handleFioAgree}
          onClose={presenter.handleFioAgree}
        />
      )}
    </S.FormGridRow>
  );
};
